import { z } from 'zod'
import { primitiveSchema } from './primitive.dto.js'

import { furnitureClassSchema } from '../enums/furniture-class.enum.js'
import { browserAssetUrlSchema } from '../helpers/asset-ref.js'

/**
 * A catalog furniture item for a tenant. Its shape is a free-form list of
 * drawing `primitives` designed in the furniture editor — there is no fixed
 * "kind" and no per-shape dimension fields, except two pieces that are not
 * drawn. A dance floor is the square its default size produces, and a plan may
 * grow it by its increment and by nothing else. A riser is one unit, and a plan
 * lays it out as a runway of whole units.
 *
 * Two different numbers here are both called "capacity" in conversation, so the
 * schema keeps them apart: `quantity` is how many of the piece exist to place,
 * and `maxCapacity` is how many people one of them seats.
 */
export const furnitureItemDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Broad category — drives programmatic layouts (e.g. seating charts). */
  class: furnitureClassSchema,
  primitives: z.array(primitiveSchema),
  /** Browser-usable thumbnail (signed GET, external, or data URL); null if none. */
  imageUrl: browserAssetUrlSchema.nullable(),
  /**
   * The venue that owns this piece. Null for a vendor's, for a client's
   * brought-in one, and — until the move to venue-owned furniture finishes —
   * for a catalog piece offered at no venue at all.
   */
  venueId: z.string().uuid().nullable(),
  /** How many the owner stocks. Used for vendor-owned items (the availability cap
   * when they appear in a linked venue's events); null for tenant catalog items. */
  quantity: z.number().int().nonnegative().nullable(),
  /** Per-unit price in whole cents (e.g. 1250 = $12.50); null if unpriced. */
  price: z.number().int().nonnegative().nullable(),
  /** Short freeform note (material, color, etc.); null if none. */
  description: z.string().nullable(),
  /**
   * How many chairs this table seats. Null for everything that is not a table,
   * and for a table nobody has said yet — which is every table until someone
   * fills it in, and is why the seat totals on a plan count only the tables
   * that carry one. An absent number stays absent rather than becoming a guess.
   */
  maxCapacity: z.number().int().positive().nullable(),
  /**
   * How big a new dance floor is, in feet. Null for everything that is not a
   * dance floor. The placed copy starts here; changing it later does not
   * resize dance floors already on a plan, the same way a redrawn chair does
   * not.
   */
  danceFloorDefaultFeet: z.number().positive().nullable(),
  /**
   * How much, in feet, a placed dance floor may grow by. The sizes are the
   * default plus whole steps of this, and both sides stay equal. Null for
   * everything that is not a dance floor.
   */
  danceFloorIncrementFeet: z.number().positive().nullable(),
  /**
   * How wide one riser unit is, in feet — the width of every runway made of
   * it. Null for everything that is not a riser.
   */
  riserUnitWidthFeet: z.number().positive().nullable(),
  /**
   * How long one riser unit is, in feet. A placed runway is a whole number of
   * these end to end, and uses that many from stock. Null for everything that
   * is not a riser.
   */
  riserUnitLengthFeet: z.number().positive().nullable(),
  /**
   * What the venue calls this piece. Empty means nobody has said yet — the
   * state every imported piece starts in, and the one worth finding.
   *
   * Deliberately not the input schema: that one caps how many tags a person may
   * add, and a cap on the way *out* turns a row with one tag too many into a
   * failed response for the whole list rather than one odd row. What is read
   * back is whatever is stored.
   */
  tags: z.array(z.string()),
})

export type FurnitureItemDto = z.infer<typeof furnitureItemDtoSchema>

export const furnitureItemListDtoSchema = z.object({
  items: z.array(furnitureItemDtoSchema),
  total: z.number().int().nonnegative(),
})

export type FurnitureItemListDto = z.infer<typeof furnitureItemListDtoSchema>
